import { computed, ComputedRef, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useCoreStore, useVisitorsStore } from '@/shared/store'
import { useFeaturesStore } from '@/shared/store/feature'
import { setFormatDate } from '@/shared/utils'
import type { VisitorType, PhoneInfo } from '@/shared/types/store.types'
import type { CountryWithDetailsType } from '@/shared/types/other.types'

interface UsePersonStateOptions {
  initialVisitor: VisitorType
  isHolder: boolean
  isEdit: boolean
}

export const usePersonState = (options: UsePersonStateOptions) => {
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const featuresStore = useFeaturesStore()

  const { nationalities, countries } = storeToRefs(coreStore)

  const localNationalities = computed(() =>
    nationalities.value?.map((nat) => ({
      name: nat.desc,
      iso: nat.flag,
      ...nat,
    }))
  )

  const localCountries = computed((): CountryWithDetailsType[] =>
    countries.value?.map<CountryWithDetailsType>((country) => ({
      name: country.desc,
      iso: country.flag,
      ...country,
    }))
  )

  const person = ref<VisitorType>({
    ...options.initialVisitor,
    fullName: `${options.initialVisitor.firstName} ${options.initialVisitor.lastName}`.trim(),
  })

  const isDisabledProperties = options.isEdit && !!person.value.visaValidityDate

  const passportMinDate = ref(new Date(setFormatDate(new Date(), 'en')))

  const isMaritalStatusEnabled: ComputedRef<boolean> = computed(
    () => featuresStore.isMaritalStatusEnabled
  )

  const nationalityIso = computed(
    () => localCountries.value.find((c) => c.code === person.value.nationality)?.iso ?? ''
  )

  const selectedCountry = ref<CountryWithDetailsType | undefined>()
  const selectedNationality = ref<CountryWithDetailsType | undefined>()

  const setSelectedCountry = (code: string | null): void => {
    selectedCountry.value = localCountries.value.find((country) => country.code === code)
  }

  const setSelectedNationality = (code: string | null): void => {
    selectedNationality.value = localNationalities.value.find(
      (nationality) => nationality.code === code
    )
  }

  const updateContactsSame = (newValue: boolean): void => {
    if (options.isHolder) return
    const { email } = visitorsStore.holder
    if (newValue) {
      person.value.email = email
    } else {
      person.value.email =
        options.initialVisitor.email !== email ? options.initialVisitor.email : email
    }
  }

  const setPhoneInfo = (phoneInfo: PhoneInfo): void => {
    person.value.phoneInfo = { ...phoneInfo }
  }

  watch(selectedCountry, () => {
    person.value.country = selectedCountry.value?.code || null
  })

  watch(localNationalities, () => setSelectedNationality(person.value.nationality), {
    immediate: true,
  })
  watch(localCountries, () => setSelectedCountry(person.value.country), { immediate: true })

  watch(
    () => person.value.contactsSame,
    (isContactsSame) => {
      if (options.isHolder) return
      updateContactsSame(isContactsSame)
    },
    { immediate: true }
  )

  return {
    person,
    selectedCountry,
    selectedNationality,
    setSelectedCountry,
    setSelectedNationality,
    localCountries,
    localNationalities,
    nationalityIso,
    isDisabledProperties,
    isMaritalStatusEnabled,
    passportMinDate,
    setPhoneInfo,
  }
}
